import { COMMUNES, MARITAL_STATUS } from '../types';
import { Subject } from './types';

export interface FieldConfig {
  name: string;
  label: string;
  type: 'text' | 'email' | 'date' | 'tel' | 'checkbox' | 'textarea' | 'select' | 'autocomplete' | 'year';
  placeholder?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  /** Máximo de caracteres para campos de texto. Por defecto MAX_FIELD_LENGTH (100). */
  maxLength?: number;
  options?: { label: string; value: string }[];
  min?: number;
  multiple?: boolean;
  /** Mostrar solo cuando el colegio seleccionado es "Otro" (opción de la BD) */
  showWhenSchoolIsOtro?: boolean;
  /** Condición para mostrar el campo basado en el valor/label de otro campo */
  showWhen?: {
    field: string;
    hasLabel: string;
  };
}

/** Límite por defecto de caracteres para los campos de texto del formulario. */
export const MAX_FIELD_LENGTH = 100;

export interface StepConfig {
  stepNumber: number;
  title: string;
  description?: string;
  fields: FieldConfig[];
}

export const generateStep2Config = (subjects: Subject[]): StepConfig => ({
  stepNumber: 2,
  title: 'Pruebas a rendir',
  description: 'Elige las áreas que te interesa preparar con nosotros',
  fields: [
    ...subjects.map((subject) => ({
      name: `subject_${subject.id}`,
      label: subject.name,
      type: 'checkbox' as const,
    })),
    {
      name: 'testsComment',
      label: 'Cuéntanos más sobre tus objetivos:',
      type: 'textarea' as const,
      placeholder: 'Me interesa el área de ingeniería...',
      multiline: true,
      rows: 4,
    },
  ],
});

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

export const getEnrollmentSteps = (
  subjects: Subject[],
  locationOptions: { label: string; value: string }[] = [],
  schoolOptions: { label: string; value: string }[] = [],
): StepConfig[] => [
  {
    stepNumber: 1,
    title: 'Datos del alumno',
    fields: [
      {
        name: 'name',
        label: 'Nombre',
        type: 'text',
        required: true,
      },
      {
        name: 'lastname',
        label: 'Apellido Paterno',
        type: 'text',
        required: true,
      },
      {
        name: 'motherLastname',
        label: 'Apellido Materno',
        type: 'text',
        required: true,
      },
      {
        name: 'rut',
        label: 'RUT',
        type: 'text',
        placeholder: 'RUT sin puntos ni guión',
        required: true,
      },
      {
        name: 'birthDate',
        label: 'Fecha de nacimiento',
        type: 'date',
        required: true,
      },
      {
        name: 'email',
        label: 'Correo',
        type: 'email',
        required: true,
      },
      {
        name: 'phoneNumber',
        label: 'Teléfono',
        type: 'tel',
        required: true,
      },
      {
        name: 'schoolId',
        label: 'Colegio',
        type: 'autocomplete',
        required: true,
        options: schoolOptions,
      },
      {
        name: 'otherSchoolName',
        label: 'Nombre del otro colegio',
        type: 'text',
        placeholder: 'Ingresa el nombre de tu colegio',
        required: false,
        showWhenSchoolIsOtro: true,
      },
    ],
  },
  generateStep2Config(subjects),
  {
    stepNumber: 3,
    title: 'Sede y nivel',
    description: 'Elige una o más de nuestras sedes, el nivel y el año que cursarás',
    fields: [
      {
        name: 'preferredLocationIds',
        label: 'Sede(s) (puedes elegir varias)',
        type: 'select',
        required: true,
        multiple: true,
        options: locationOptions,
      },
      {
        name: 'desiredClassYear',
        label: 'Nivel a cursar',
        type: 'select',
        required: true,
        options: [
          { label: 'IV Medio', value: 'IV Medio' },
          { label: 'III Medio', value: 'III Medio' },
          { label: 'Egresados', value: 'Egresados' },
        ],
      },
      {
        name: 'inscriptionYear',
        label: 'Año',
        type: 'select',
        required: true,
        options: [
          { label: String(currentYear), value: String(currentYear) },
          { label: String(nextYear), value: String(nextYear) },
        ],
      },
    ],
  },
  {
    stepNumber: 4,
    title: 'Datos del apoderado',
    fields: [
      {
        name: 'representativeName',
        label: 'Nombre',
        type: 'text',
        required: true,
      },
      {
        name: 'representativeLastname',
        label: 'Apellido Paterno',
        type: 'text',
        required: true,
      },
      {
        name: 'representativeMotherLastname',
        label: 'Apellido Materno',
        type: 'text',
        required: true,
      },
      {
        name: 'representativeRut',
        label: 'RUT',
        type: 'text',
        placeholder: 'RUT sin puntos ni guión',
        required: true,
      },
      {
        name: 'representativeBirthDate',
        label: 'Fecha de nacimiento',
        type: 'date',
        required: true,
      },
      {
        name: 'representativeEmail',
        label: 'Correo',
        type: 'email',
        required: true,
      },
      {
        name: 'representativeNationality',
        label: 'Nacionalidad',
        type: 'autocomplete',
        required: true,
        options: [{ label: 'Chilena', value: 'Chilena' }, { label: 'Extranjera', value: 'Extranjera' }],
      },
      {
        name: 'representativeMaritalStatus',
        label: 'Estado civil',
        type: 'select',
        required: true,
        options: MARITAL_STATUS.map((status) => ({ label: status.name, value: status.id })),
      },
      {
        name: 'representativePhoneNumber',
        label: 'Teléfono',
        type: 'tel',
        required: true,
      },
      {
        name: 'representativeAddress',
        label: 'Dirección',
        type: 'text',
        required: true,
      },
      {
        name: 'representativeCommune',
        label: 'Comuna',
        type: 'autocomplete',
        required: true,
        options: [
          ...COMMUNES
            .map((commune) => ({ label: commune.name, value: commune.id }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
          { label: 'Otro', value: 'otro' },
        ],
      },
      {
        name: 'representativeOtherCommune',
        label: 'Nombre de la otra comuna',
        type: 'text',
        placeholder: 'Ingresa el nombre de tu comuna',
        required: false,
        showWhen: {
          field: 'representativeCommune',
          hasLabel: 'Otro',
        },
      },
    ],
  },
  {
    stepNumber: 5,
    title: 'Datos del sostenedor',
    fields: [
      {
        name: 'isSameAsRepresentative',
        label: 'Es el mismo que el apoderado',
        type: 'checkbox',
      },
      {
        name: 'supporterName',
        label: 'Nombre',
        type: 'text',
        required: true,
      },
      {
        name: 'supporterLastname',
        label: 'Apellido Paterno',
        type: 'text',
        required: true,
      },
      {
        name: 'supporterMotherLastname',
        label: 'Apellido Materno',
        type: 'text',
        required: true,
      },
      {
        name: 'supporterRut',
        label: 'RUT',
        type: 'text',
        placeholder: 'RUT sin puntos ni guión',
        required: true,
      },
      {
        name: 'supporterBirthDate',
        label: 'Fecha de nacimiento',
        type: 'date',
        required: true,
      },
      {
        name: 'supporterEmail',
        label: 'Correo',
        type: 'email',
        required: true,
      },
      {
        name: 'supporterNationality',
        label: 'Nacionalidad',
        type: 'autocomplete',
        required: true,
        options: [{ label: 'Chilena', value: 'Chilena' }, { label: 'Extranjera', value: 'Extranjera' }],
      },
      {
        name: 'supporterMaritalStatus',
        label: 'Estado civil',
        type: 'select',
        required: true,
        options: MARITAL_STATUS.map((status) => ({ label: status.name, value: status.id })),
      },
      {
        name: 'supporterPhoneNumber',
        label: 'Teléfono',
        type: 'tel',
        required: true,
      },
      {
        name: 'supporterAddress',
        label: 'Dirección',
        type: 'text',
        required: true,
      },
      {
        name: 'supporterCommune',
        label: 'Comuna',
        type: 'autocomplete',
        required: true,
        options: [
          ...COMMUNES
            .map((commune) => ({ label: commune.name, value: commune.id }))
            .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' })),
          { label: 'Otro', value: 'otro' },
        ],
      },
      {
        name: 'supporterOtherCommune',
        label: 'Nombre de la otra comuna',
        type: 'text',
        placeholder: 'Ingresa el nombre de tu comuna',
        required: false,
        showWhen: {
          field: 'supporterCommune',
          hasLabel: 'Otro',
        },
      },
    ],
  },
  {
    stepNumber: 6,
    title: '¿Cómo nos conociste?',
    fields: [
      {
        name: 'leadSource',
        label: '¿Cómo nos conociste?',
        type: 'select',
        required: true,
        options: [
          { label: 'Recomendación de un amigo o familiar', value: 'recomendacion' },
          { label: 'Hermano(a)', value: 'hermano' },
          { label: 'Página web', value: 'pagina_web' },
          { label: 'Embajador(a)', value: 'embajador' },
          { label: 'Instagram', value: 'instagram' },
          { label: 'Profesor(a)', value: 'profesor' },
          { label: 'Centro de alumnos', value: 'centro_alumnos' },
        ],
      },
      {
        name: 'leadName',
        label: 'Nombre',
        type: 'text',
        required: true,
      },
      {
        name: 'ambassadorCode',
        label: 'Código',
        type: 'text',
        required: true,
      },
    ],
  },
];

export const enrollmentSteps: StepConfig[] = getEnrollmentSteps([]);
